import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/components/base.component';
import { AutoDrawModels } from 'src/app/core/models';
import { CheckForm } from '../autodraw/check-form.service';
import { Store } from '@ngrx/store';
import { CloseAction, OpenAction, SpinnerState } from 'src/app/store/spinner';
import { TranslateService } from '@ngx-translate/core';
import { AutoDrawService } from 'src/app/api/routes/autodraw.service';
import { catchError, map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ErrorHandlerService } from 'src/app/api/error-handler.service';
import { Observable, of } from 'rxjs';
import { environment } from "src/environments/environment";
import { NotifyModels } from 'src/app/core/models/notify';
import { AUTO_DRAW_ENUM, NOTIFY_ENUM } from 'src/app/core/enum';
import { NotifyService } from 'src/app/api/routes/notify.service';
import * as bootstrap from 'bootstrap';
import { Router } from '@angular/router';
import { AlertService } from 'src/app/shared/alert.service';

@Component({
	selector: 'app-contact-us',
	templateUrl: './contact-us.component.html',
	styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent extends BaseComponent implements OnInit {
	@ViewChild('closeModalBtn', { read: ElementRef }) closeModalBtn!: ElementRef;

	// 參數資料
	@Input() project: AutoDrawModels.Project = new AutoDrawModels.Project();
	@Input() needCreateProject: boolean = false;

	// 於environments中設定收件人信箱
	public RECEIVERS: {receiverName: string, receiverMail: string}[] = environment.mailConfig.receivers;

	// 聯絡我們參數備註開啟狀態
	public memoStatusList: boolean[] = [];

	public currentRoute: string = '';

	// Enums
	public NOTIFY_METHOD = NOTIFY_ENUM.NOTIFY_METHOD;
	public MEMO_LENGTH = AUTO_DRAW_ENUM.MEMO_LENGTH;
	public PRODUCT_CATEGORY = AUTO_DRAW_ENUM.PRODUCT_CATEGORY;
	public ALGO_TYPE = AUTO_DRAW_ENUM.ALGO_TYPE;

	constructor(
		private autoDrawServ: AutoDrawService,
		private cd: ChangeDetectorRef,
		private spinnerState: Store<SpinnerState>,
		private errServ: ErrorHandlerService,
		private checkFormServ: CheckForm,
		private translate: TranslateService,
		private notifyServ: NotifyService,
		private router: Router,
		private alertServ: AlertService
	) { super(); }


	ngOnInit(): void {
		console.log('contact_us')
		const path = this.router.url.split('?')[0];
		const pathSegments = path.split('/');
		this.currentRoute = pathSegments[pathSegments.length - 1];
	}

	override ngOnDestroy() {
		// 銷毀元件時關閉modal
		this.closeModal();
	}

	/**
	 * 開啟modal
	 */
	openModal() {
		const modalELement = document.getElementById('contactUs');
		const contactUsModal = new bootstrap.Modal(<Element>modalELement, {
			keyboard: false
		});
		// 設置參數備註開啟狀態
		this.memoStatusList = new Array(this.project.tims.length).fill(false);
		contactUsModal.show();
	}

	/**
	 * 檢查字串是否為空值
	 * @param str `string | null`
	 * @returns `boolean` true 非空值, false 空值
	 */
	public checkNull(str: string | null): boolean {
		const processStr = str ? str.trim() : "";
		const result = processStr.length > 0;
		return result;
	}


	/**
	 * 送出表單參數
	 */
	public submitParam(algoType: number) {
		const param = {
			product_category: this.PRODUCT_CATEGORY.tim,
			tims: this.project.tims
		}
		const loadingMsg = this.translate.instant('LOADING.CREATE')
		this.spinnerState.dispatch(new OpenAction(loadingMsg));
		return this.autoDrawServ.create_records(param).pipe(
			takeUntil(this.unsubscribe$),
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((recordId: number) => {
				this.project.id = recordId;
				this.spinnerState.dispatch(new CloseAction());
				this.cd.markForCheck();
			})
		);
	}


	/**
	 * 提交聯絡我們表單
	 */
	public submitContact() {
		// 檢查表單輸入
		const hasCompanyName = this.checkNull(this.project.company_name);
		const hasApplicantName = this.checkNull(this.project.applicant_name);
		const hasApplicant = this.checkNull(this.project.applicant_email);
		const hasApplicantPhone = this.checkNull(this.project.applicant_phone);
		const hasNull = !(hasCompanyName && hasApplicantName && hasApplicant && hasApplicantPhone);
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const invalidRegex = !emailRegex.test(<string>this.project.applicant_email);
		const invalidMemo = this.project.tims.some(tim => this.checkFormServ.checkMemoLength(tim, this.MEMO_LENGTH.MAX));
		if (hasNull) return alert(this.translate.instant("AUTO_DRAW.ALERT.NULL"));
		else if (invalidRegex) return alert(this.translate.instant("AUTO_DRAW.ALERT.EMAIL"));
		else if (invalidMemo) return alert(this.translate.instant("AUTO_DRAW.ALERT.MEMO", {max: this.MEMO_LENGTH.MAX}));


		let param = this.project;
		let startObser$ = this.needCreateProject ? this.submitParam(this.ALGO_TYPE.draw) : of(0);
		
		startObser$.pipe(
			mergeMap(_ => {
				const loadingMsg = this.translate.instant('LOADING.CREATE')
				this.spinnerState.dispatch(new OpenAction(loadingMsg));
				return this.autoDrawServ.update_records(param);
			}),
			takeUntil(this.unsubscribe$),
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap(updateTime => {
				this.project.updated_at = updateTime
			}),
			switchMap(_ => this.createQueue())
		).subscribe()
	}

	/**
	 * 串接API建立Notify Queue
	 * @returns `Observable<any>`
	 */
	public createQueue(): Observable<any> {
		// 組成queue參數
		const testTitle = environment.production ? '' : '(test)';	// 若為測試環境則用加上(test)
		const params: NotifyModels.QueueParam[] = [];
		let paramsContent = '';
		this.project.tims.forEach(tim => paramsContent += tim.composeParamsContent(this.translate));
		this.RECEIVERS.forEach(receiver => {
			const content = this.project.composeMailContent(receiver.receiverName, paramsContent);
			let param = {
				title: this.translate.instant('NOTIFY.CONTACT.TITLE', {test: testTitle}),
				notify_method: this.NOTIFY_METHOD.ASINK_AUTO_DRAW_CONTACT_US,
				sender_name: this.project.applicant_name || '',
				receiver_name: receiver.receiverName,
				receiver_mail: receiver.receiverMail,
				template_id: 1,		// 無影響，使用content內容
				content: content
			}
			params.push(param);
		});
		return this.notifyServ.create_records(params).pipe(
			catchError(err => {
				this.spinnerState.dispatch(new CloseAction());
				this.errServ.httpErrorHandle(err);
				return of()
			}),
			map(res => res.Data),
			tap((data) => {
				this.spinnerState.dispatch(new CloseAction());
				alert(this.translate.instant('AUTO_DRAW.ALERT.CONTACT'))
				this.closeModal();
				if(this.currentRoute === 'autodraw') {
					this.router.navigate(['/custom/product/list']);
				}
			})
		);
	}

	/**
	 * 關閉Modal
	 */
	public closeModal() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		if ( this.closeModalBtn !== undefined ) {
			this.closeModalBtn.nativeElement.click();
		}
	}
}
